// Angular imports
import { HttpClient } from '@angular/common/http';
// 3rd party imports
import { plainToClass, classToPlain } from 'class-transformer';
import { map } from 'rxjs/operators';
// cc-common imports
import { Utils } from 'cc-common';
// Constants imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';
// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';
import { FileInfoDisplayResultDto } from '../../_models/file-info-display-result-dto';
import { ProjectTaskHistoryResultDto } from '../_models/project-task-history-result-dto';
import { ProjectTaskEditorResultDto } from '../_models/project-task-editor-result-dto';
import { EmailReminderResultDto } from '../_models/email-reminder-result-dto';
import { EmailLinkResultDto } from '../_models/email-link-result-dto';
import { ProjectTaskResponsibleUsersResultDto } from '../_models/project-task-responsible-users-result-dto';
import { ProjectTaskNotificationSettingsRawDto } from '../_models/project-task-notification-settings-raw-dto';
import { LinkInternalTaskEditorResultDto } from '../_models/link-internal-task-editor-result-dto';
import { WorkOrderLinkResultDto } from '../_models/work-order-link-result-dto';
import { BaseProcessNotesResultDto } from '../../management-common/_models/notes/base-process-notes-result-dto';
import { ProjectTaskResultDto } from '../_models/project-task-result-dto';
import { ProjectTaskItemResultDto } from '../_models/project-task-item-result-dto';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../../_services/shared-services/mp-cache-registration-service";
export class ProjectTaskService {
    constructor(httpClient, cacheRegistrationService) {
        this.httpClient = httpClient;
        this.cacheRegistrationService = cacheRegistrationService;
        this._emailRemindersSource = [];
    }
    getOpenProjectTasks(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/open${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData) => plainToClass(ProjectTaskResultDto, responseData)));
    }
    getClosedProjectTasks(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/closed${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData) => plainToClass(ProjectTaskResultDto, responseData)));
    }
    getProjectTaskHistory(projectTaskId, clearCache) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/history?projectTaskId=${projectTaskId}&clearCache=${clearCache}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData) => plainToClass(ProjectTaskHistoryResultDto, responseData)));
    }
    getProjectTaskAttachments(projectTaskId, communityId) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/attachments?projectTaskId=${projectTaskId}&communityId=${communityId}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData) => plainToClass(FileInfoDisplayResultDto, responseData)));
    }
    getProjectTaskEditor(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/editor${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData) => plainToClass(ProjectTaskEditorResultDto, responseData)));
    }
    getEmailReminders(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/emailReminders${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData) => plainToClass(EmailReminderResultDto, responseData)));
    }
    downloadProjectTaskDocs(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/download/attachment${param.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData) => new Blob([responseData])));
    }
    getLinkedEmailsToProjectTask(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/email/linked${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData) => plainToClass(EmailLinkResultDto, responseData)));
    }
    unlinkEmailsFromProjectTask(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/email/unlink`;
        return this.httpClient.post(url, classToPlain(param));
    }
    linkEmailsToProjectTask(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/email/link`;
        return this.httpClient.post(url, classToPlain(param));
    }
    getInternalTaskForProjectTask(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/internaltask/link/editor${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData) => plainToClass(LinkInternalTaskEditorResultDto, responseData)));
    }
    unlinkInternalTaskForProjectTask(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/internaltask/unlink`;
        return this.httpClient.post(url, classToPlain(param));
    }
    updateProjectTaskStatus(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/status`;
        return this.httpClient.put(url, classToPlain(param));
    }
    getResponsibleUsers(baseParam) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/responsibleusers${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData) => plainToClass(ProjectTaskResponsibleUsersResultDto, responseData)));
    }
    createProjectTask(param) {
        const url = URLConstants.ccProjectTaskBaseApiUrl;
        return this.httpClient.post(url, classToPlain(param));
    }
    updateProjectTask(param) {
        const url = URLConstants.ccProjectTaskBaseApiUrl;
        return this.httpClient.put(url, classToPlain(param));
    }
    getNotificationSettings() {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/notification-settings`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData) => plainToClass(ProjectTaskNotificationSettingsRawDto, responseData)));
    }
    updateNotificationSetting(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/notification-settings`;
        return this.httpClient.put(url, classToPlain(param));
    }
    sendReminderMail(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/reminderemail`;
        return this.httpClient.post(url, classToPlain(param));
    }
    uploadProjectTaskDocument(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/attachments`;
        return this.httpClient.post(url, classToPlain(param));
    }
    getOpenWorkOrders(baseParams) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/workorders/linkable/open${baseParams.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response) => plainToClass(WorkOrderLinkResultDto, response)));
    }
    getClosedWorkOrders(baseParams) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/workorders/linkable/close${baseParams.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response) => plainToClass(WorkOrderLinkResultDto, response)));
    }
    manageWorkOrders(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/workorders/manage`;
        return this.httpClient.post(url, classToPlain(param));
    }
    getProjectTaskDetails(baseParams) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/details${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData) => plainToClass(ProjectTaskItemResultDto, responseData)));
    }
    getProjectTaskNotes(id, clearCache) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/notes?id=${id}&clearCache=${clearCache}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData) => plainToClass(BaseProcessNotesResultDto, responseData)));
    }
    addProjectTaskNotes(param) {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/notes`;
        return this.httpClient.post(url, classToPlain(param));
    }
    updateProjectTaskEmailReminders(taskId, emailReminderInfo) {
        const node = {
            taskId,
            items: emailReminderInfo
        };
        this._emailRemindersSource.push(node);
    }
    getProjectTaskEmailReminders(taskIdToSearch) {
        const source = this._emailRemindersSource.find((d) => Utils.equals(d.taskId, taskIdToSearch));
        return Utils.notNullAndDefined(source) &&
            Utils.notNullAndDefined(source.items) &&
            Utils.notNullOrEmpty(source.items)
            ? source.items
            : null;
    }
}
ProjectTaskService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function ProjectTaskService_Factory() { return new ProjectTaskService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.MPCacheRegistrationService)); }, token: ProjectTaskService, providedIn: "root" });
